// Organization branding (doc 02 §10, doc 09 §1): the org's primary colour replaces the default
// brand blue while the user works inside that organization. It is applied as CSS variables on
// the root element, so every component and portal picks it up without a re-render.
export const DEFAULT_PRIMARY = '#3454d1';

type Rgb = [number, number, number];
const parse = (hex: string): Rgb => [1, 3, 5].map((i) => parseInt(hex.slice(i, i + 2), 16)) as Rgb;
const toHex = (c: Rgb) => `#${c.map((v) => Math.round(Math.min(255, Math.max(0, v))).toString(16).padStart(2, '0')).join('')}`;
const mix = (a: Rgb, b: Rgb, t: number): Rgb => [0, 1, 2].map((i) => a[i]! + (b[i]! - a[i]!) * t) as Rgb;

/** Ten shades around the colour, the colour itself at index 6 (as in the app theme). */
export function shadesOf(hex: string): string[] {
  const base = parse(hex);
  const white: Rgb = [255, 255, 255];
  const black: Rgb = [0, 0, 0];
  const toWhite = [0.92, 0.82, 0.64, 0.46, 0.3, 0.14];
  const toBlack = [0.14, 0.26, 0.38];
  return [...toWhite.map((t) => toHex(mix(base, white, t))), hex.toLowerCase(), ...toBlack.map((t) => toHex(mix(base, black, t)))];
}

/** Relative luminance (WCAG) — decides whether text on the colour is white or dark. */
function luminance(hex: string): number {
  const [r, g, b] = parse(hex).map((v) => {
    const c = v / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  }) as Rgb;
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

const NAMES = ['--mantine-color-brand', '--mantine-primary-color'];
let applied: string[] = [];

export function applyBranding(primary: string | null | undefined, dark: boolean): void {
  const root = document.documentElement;
  for (const name of applied) root.style.removeProperty(name);
  applied = [];
  if (!primary || !/^#[0-9a-f]{6}$/i.test(primary) || primary.toLowerCase() === DEFAULT_PRIMARY) return;

  const shades = shadesOf(primary);
  const [r, g, b] = parse(primary);
  const set = (name: string, value: string) => {
    root.style.setProperty(name, value);
    applied.push(name);
  };
  const filled = shades[dark ? 7 : 6]!;
  for (const prefix of NAMES) {
    shades.forEach((shade, i) => set(`${prefix}-${i}`, shade));
    set(`${prefix}-filled`, filled);
    set(`${prefix}-filled-hover`, shades[dark ? 8 : 7]!);
    set(`${prefix}-light`, `rgba(${r}, ${g}, ${b}, ${dark ? 0.18 : 0.1})`);
    set(`${prefix}-light-hover`, `rgba(${r}, ${g}, ${b}, ${dark ? 0.24 : 0.14})`);
    set(`${prefix}-light-color`, shades[dark ? 3 : 7]!);
    set(`${prefix}-outline`, shades[dark ? 4 : 6]!);
    set(`${prefix}-outline-hover`, `rgba(${r}, ${g}, ${b}, 0.06)`);
    set(`${prefix}-text`, shades[dark ? 4 : 6]!);
  }
  set('--mantine-primary-color-contrast', luminance(filled) > 0.5 ? '#0f172a' : '#ffffff');
  set('--tl-primary', shades[dark ? 4 : 6]!);
}
